import { type ReactElement, type ReactNode, useContext } from 'react';
import { firstHeldPermission, PermissionContext, PermissionGuard } from '../ui';
import type { NavItem } from './navConfig';

/**
 * The `renderNavItem` decorator F5-b WI-1.1's `Sidebar` seam consumes (F5-b spec FR-4/FR-5): wrap each nav
 * entry's rendered element in the shipped `PermissionGuard`, gated on the entry's keys.
 *
 * No new primitive is built (F5-b spec FR-5, the load-bearing scoping fact). The shipped `PermissionGuard`
 * IS §9's nav rule: an absent key resolves `denied-permanent` → the guard returns `null` (the entry
 * renders nothing), a contextual block resolves `denied-contextual` → the guard renders it disabled
 * with its reason joined to the accessible name and surfaced on hover/focus. Gating reads the
 * `PermissionProvider` that `AuthenticatedGate` already installed from the `/me` grants — this
 * decorator is a thin wrapper, the decision lives entirely in the guard. An entry with more than one
 * key (Activities, F3-a FR-V7) is gated on the first key the session holds, so it shows when any of them is held.
 */
export function renderGatedNavItem(item: NavItem, element: ReactElement): ReactNode {
    return <GatedNavItem element={element} item={item} key={item.id} />;
}

function GatedNavItem({ item, element }: { readonly item: NavItem; readonly element: ReactElement }) {
    return (
        <NavEntryGuard key={item.id} item={item}>
            {element}
        </NavEntryGuard>
    );
}

interface NavEntryGuardProps {
    readonly item: NavItem;
    readonly children: ReactElement;
}

/**
 * An entry with several keys shows when any one is held (F3-a FR-V7): the guard is handed the first
 * held key, or the first key when none is, so the decision is still `PermissionGuard`'s. A component
 * rather than a call in `renderGatedNavItem`, because the resolver is read through context and
 * `Sidebar` calls the decorator inside a `map`, where a hook may not be called.
 */
function NavEntryGuard({ item, children }: NavEntryGuardProps) {
    const resolve = useContext(PermissionContext);
    return (
        <PermissionGuard permission={firstHeldPermission(resolve, item.permissionKeys)}>
            {children}
        </PermissionGuard>
    );
}
